export interface ApiError {
  message: string;
  detail?: string;
  status?: number;
}

interface AxiosErrorResponse {
  response?: {
    data?: {
      detail?: string;
    };
    status?: number;
  };
  message?: string;
}

/**
 * Extract error message from various error types
 * @param error - Error object (could be AxiosError, Error, or unknown)
 * @param fallback - Fallback message if error details cannot be extracted
 * @returns Structured error information
 */
export const extractErrorMessage = (
  error: unknown,
  fallback: string = 'An unexpected error occurred'
): ApiError => {
  const err = error as AxiosErrorResponse;

  // Handle Axios-like errors with response.data.detail
  if (err.response?.data?.detail) {
    return {
      message: err.response.data.detail,
      detail: err.response.data.detail,
      status: err.response.status
    };
  }

  // Handle errors with just a message property
  if (err.message) {
    return {
      message: err.message,
      detail: err.message
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    return {
      message: error.message || fallback,
      detail: error.message
    };
  }

  // Handle unknown errors
  return {
    message: fallback,
    detail: String(error)
  };
};

/**
 * Extract HTTP status code from error object
 */
export const getErrorStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') return null;

  const err = error as AxiosErrorResponse;
  return err.response?.status ?? null;
};

/**
 * Check if error is a specific HTTP status code
 */
export const isHttpError = (error: unknown, statusCode: number): boolean => {
  return getErrorStatus(error) === statusCode;
};